/**
 * BuildingLoaderService
 * Converts database building/hangar records to 3D rendering format
 */

import type { HangarRow, AreaType } from '@/types/hangars'
import type { UnitType } from '@/types/units'
import type { Building3DConfig } from './SceneBuilder'
import type { HangarWith3DData, UnitFor3D } from '@/lib/api/hangars'
import { CoordinateService } from './CoordinateService'
import { createBoxUnitEdges } from '@/lib/templates/base/edges'
import { createDoubleRowEdges } from '@/lib/templates/double-row/edges'
import { createTHangarEdges } from '@/lib/templates/standard-t-hangar/edges'
import { createDefaultTHangarDoorSpec, type THangarParams } from '@/lib/templates/standard-t-hangar/params'
import { createNestedTHangarEdges } from '@/lib/templates/nested-t-hangar/edges'
import type { GeneratedBuilding, GeneratedUnit, DoorSpec, DoorType, UnitDoorsConfig, UnitEdge } from '@/lib/templates/types'
import { normalizeFloorColor, normalizeWallColor, normalizeElevation, doorsArrayToConfig, type RawBuildingSettings, type RawTypeConfig } from './utils/settingsNormalizer'

// Type definitions moved to utils/settingsNormalizer.ts:
// - RawBuildingSettings (handles camelCase/snake_case colors, position, doors)
// - RawTypeConfig (template, unit_count, wall_thickness, per-row overrides)

/** Extended Building3DConfig with template data for rendering */
export interface Building3DConfigExtended extends Building3DConfig {
  /** Reconstructed GeneratedBuilding for orchestrators */
  generatedBuilding?: GeneratedBuilding
  /** Reconstructed GeneratedUnits for orchestrators */
  generatedUnits?: GeneratedUnit[]
}

/**
 * Service for loading and transforming building data for 3D rendering
 */
export class BuildingLoaderService {
  /**
   * Convert database buildings to 3D config format
   * @param buildings - Buildings from database
   * @param centerCoords - Scene center coordinates [lng, lat]
   * @returns Building3DConfig array ready for SceneBuilder
   */
  static transformBuildings(
    buildings: HangarRow[],
    centerCoords: [number, number]
  ): Building3DConfig[] {
    return buildings.map((building) =>
      BuildingLoaderService.transformBuilding(building, centerCoords)
    )
  }

  /**
   * Convert a single database building to 3D config format
   * @param building - Building from database
   * @param centerCoords - Scene center coordinates [lng, lat]
   * @returns Building3DConfig ready for SceneBuilder
   */
  static transformBuilding(
    building: HangarRow,
    centerCoords: [number, number]
  ): Building3DConfig {
    // Get building settings from JSON column
    const settings = (building.settings || {}) as RawBuildingSettings

    // Calculate building elevation (Y-axis) using normalizer
    const elevationFt = normalizeElevation(building.altitude)

    // Use position from settings if available, otherwise calculate from lat/lng
    let position: [number, number, number]
    if (settings.position && Array.isArray(settings.position)) {
      // Use stored position but apply current elevation
      position = [settings.position[0], elevationFt, settings.position[2]]
    } else {
      // Calculate building position relative to scene center
      // Building has its own lat/lng, we need to convert to feet offset from center
      // Database may return strings, so ensure numbers
      const lng = Number(building.longitude)
      const lat = Number(building.latitude)
      if (!isNaN(lng) && !isNaN(lat)) {
        const buildingCoords: [number, number] = [lng, lat]
        const [x, _, z] = CoordinateService.lngLatToScenePosition(buildingCoords, centerCoords)
        position = [x, elevationFt, z]
      } else {
        // Default position at origin if no valid coordinates
        position = [0, elevationFt, 0]
      }
    }

    // Extract template type from type_config
    const typeConfig = building.type_config as Record<string, unknown> | undefined
    const templateType = typeConfig?.template as 'single_unit_community' | 'single_row' | 'double_row' | 'outdoor_rectangular' | 'arch_hangar' | undefined

    const config = {
      id: building.id,
      name: building.name,
      code: building.area_code || '',
      // Dimensions are stored in feet in database (convert from string if needed)
      width: Number(building.exterior_width) || 100,
      depth: Number(building.exterior_depth) || 80,
      height: Number(building.exterior_height) || 30,
      wallThickness: settings.wallThickness || 2,
      // Rotation in degrees from database (convert from string if needed)
      rotation: Number(building.rotation) || 0,
      // Position relative to scene center [x, y, z] in feet
      position,
      // Colors from settings using normalizer (handles both camelCase and snake_case)
      floorColor: normalizeFloorColor(settings),
      wallColor: normalizeWallColor(settings),
      // Template info for template-aware rendering
      templateType,
      typeConfig,
      settings: building.settings as Record<string, unknown> | undefined,
    }

    return config
  }

  /**
   * Get default view settings for a location
   * @param location - Location with settings JSON
   * @returns View settings for Mapbox camera
   */
  static getLocationViewSettings(location: {
    latitude: number
    longitude: number
    settings?: Record<string, unknown>
  }): {
    center: [number, number]
    zoom: number
    pitch: number
    bearing: number
  } {
    // Check if location has custom view settings
    const locationSettings = location.settings || {}
    const viewSettings = locationSettings.viewSettings as Record<string, unknown> | undefined

    if (viewSettings) {
      return {
        center: (viewSettings.center as [number, number]) || [location.longitude, location.latitude],
        zoom: (viewSettings.zoom as number) || 17,
        pitch: (viewSettings.pitch as number) || 60,
        bearing: (viewSettings.bearing as number) || 0,
      }
    }

    // Default view settings centered on location
    return {
      center: [location.longitude, location.latitude],
      zoom: 17,
      pitch: 60,
      bearing: 0,
    }
  }

  // ============================================================================
  // TEMPLATE-AWARE BUILDING TRANSFORMATION (for 3D rendering with units/doors)
  // ============================================================================

  /**
   * Transform a building with units to extended 3D config with template data
   * This enables template-aware rendering with units and doors
   *
   * @param building - Building with units from database
   * @param centerCoords - Scene center coordinates [lng, lat]
   * @returns Extended Building3DConfig with GeneratedBuilding and GeneratedUnit data
   */
  static transformBuildingWith3DData(
    building: HangarWith3DData,
    centerCoords: [number, number]
  ): Building3DConfigExtended {
    // Get base config
    const baseConfig = BuildingLoaderService.transformBuilding(building, centerCoords)

    // Get settings and type_config using normalized types
    const settings = (building.settings || {}) as RawBuildingSettings
    const typeConfig = (building.type_config || {}) as RawTypeConfig

    // Check if this is a template-aware building
    const templateType = typeConfig.template

    // Debug: Log template info (always log to help debug rendering issues)
    console.log(`🏗️ BuildingLoaderService: "${building.name}"`, {
      rawTypeConfig: building.type_config,
      parsedTemplate: templateType,
      unitsCount: building.units?.length || 0,
      unitCodes: building.units?.map(u => u.unit_code),
      typeConfigType: typeof building.type_config,
    })

    if (!building.units || building.units.length === 0) {
      // Outdoor infrastructure areas (fuel_depot, exit_point) have no allocatable
      // units but still need template-aware rendering (border + optional floor fill).
      if (templateType === 'outdoor_rectangular') {
        return {
          ...baseConfig,
          generatedBuilding: BuildingLoaderService.reconstructGeneratedBuilding(building, settings, typeConfig),
          generatedUnits: [],
        }
      }
      // No units - return base config (can't generate collision configs)
      if (import.meta.env.DEV) {
        console.log(`🏗️ Building ${building.name}: Skipping generatedUnits (no units)`)
      }
      return baseConfig
    }

    // Reconstruct GeneratedBuilding from database
    const generatedBuilding = BuildingLoaderService.reconstructGeneratedBuilding(building, settings, typeConfig)

    // Reconstruct GeneratedUnits from database
    const generatedUnits = BuildingLoaderService.reconstructGeneratedUnits(
      building.units,
      building.area_code || 'UNIT',
      typeConfig,
      settings
    )

    // Always log unit details to debug rendering issues
    console.log(`🏗️ Building ${building.name}: Created ${generatedUnits.length} generatedUnits`, {
      units: generatedUnits.map(u => ({
        code: u.code,
        offset_x: u.offset_x,
        offset_y: u.offset_y,
        width: u.interior_width,
        depth: u.interior_depth,
        rotation: u.unit_rotation,
        edgeCount: u.edges?.length || 0,
        edgeTypes: u.edges?.map(e => `${e.side}:${e.type}`) || [],
      })),
    })

    // Also log raw unit data from database to compare
    console.log(`🏗️ Building ${building.name}: Raw units from DB`, {
      rawUnits: building.units.map(u => ({
        code: u.unit_code,
        offset_x: u.offset_x,
        offset_y: u.offset_y,
        rotation: u.unit_rotation,
      })),
    })

    return {
      ...baseConfig,
      generatedBuilding,
      generatedUnits,
    }
  }

  /**
   * Transform multiple buildings with units for 3D rendering
   */
  static transformBuildingsWith3DData(
    buildings: HangarWith3DData[],
    centerCoords: [number, number]
  ): Building3DConfigExtended[] {
    return buildings.map(building =>
      BuildingLoaderService.transformBuildingWith3DData(building, centerCoords)
    )
  }

  /**
   * Reconstruct GeneratedBuilding from database record
   */
  private static reconstructGeneratedBuilding(
    building: HangarWith3DData,
    settings: RawBuildingSettings,
    typeConfig: RawTypeConfig
  ): GeneratedBuilding {
    const isOutdoor = typeConfig.template === 'outdoor_rectangular'

    return {
      name: building.name,
      code: building.area_code || '',
      type: building.type as AreaType,
      exterior_width: Number(building.exterior_width) || 100,
      exterior_depth: Number(building.exterior_depth) || 80,
      exterior_height: Number(building.exterior_height) || 30,
      latitude: Number(building.latitude) || 0,
      longitude: Number(building.longitude) || 0,
      rotation: (Number(building.rotation) || 0) * Math.PI / 180, // Convert degrees to radians
      door_width: Number(building.door_width) || 40,
      door_height: Number(building.door_height) || 16,
      settings: {
        // For outdoor areas, preserve null floor_color (= no floor fill)
        // For hangars, use normalizer with fallback
        floor_color: isOutdoor
          ? (settings.floor_color ?? settings.floorColor ?? null)
          : normalizeFloorColor(settings),
        wall_color: normalizeWallColor(settings),
        doors: settings.doors || [],
        // Outdoor-specific visual settings
        border_color: settings.border_color || settings.borderColor,
        border_style: settings.border_style || settings.borderStyle,
        border_width_ft: settings.border_width_ft ?? settings.borderWidthFt,
        floor_opacity: settings.floor_opacity ?? settings.floorOpacity,
      },
      type_config: typeConfig as Record<string, unknown>,
    }
  }

  /**
   * Reconstruct GeneratedUnits from database records
   * Creates edge classifications based on unit positions
   * Reads door configuration from building settings for all 4 walls
   * Uses appropriate edge creation function based on template type
   */
  private static reconstructGeneratedUnits(
    units: UnitFor3D[],
    buildingCode: string,
    typeConfig: RawTypeConfig,
    settings: RawBuildingSettings
  ): GeneratedUnit[] {
    // Filter out subunits — only main units participate in edge classification
    const mainUnits = units.filter(u => u.parent_unit_id == null)
    const unitCount = mainUnits.length
    const templateType = typeConfig.template

    // Convert flattened doors array to per-wall config using normalizer
    const buildingDoorConfig = doorsArrayToConfig(settings.doors)

    // For double_row, group units by row to get per-row counts
    let rowACounts = 0
    let rowBCounts = 0
    if (templateType === 'double_row') {
      for (const unit of mainUnits) {
        const rowId = BuildingLoaderService.extractRowIdFromUnitCode(unit.unit_code)
        if (rowId === 'A') rowACounts++
        else if (rowId === 'B') rowBCounts++
      }
    }

    return mainUnits.map((unit, index) => {
      // Determine door config based on template type
      let doorConfig: UnitDoorsConfig

      if (templateType === 'double_row') {
        // For double_row, use per-unit door overrides from typeConfig
        const rowId = BuildingLoaderService.extractRowIdFromUnitCode(unit.unit_code)
        const unitIndexInRow = BuildingLoaderService.extractUnitIndexFromCode(unit.unit_code)

        // Get overrides for this row
        const overrides = rowId === 'A' ? typeConfig.row_a_overrides : typeConfig.row_b_overrides
        const unitOverride = overrides?.find(o => o.index === unitIndexInRow)

        // Use override doors if available, otherwise use unit-level defaults
        if (unitOverride?.doors && Object.keys(unitOverride.doors).length > 0) {
          doorConfig = unitOverride.doors
        } else if (unit.door_width && unit.door_height) {
          // Fall back to unit-level front door
          doorConfig = {
            front: [{
              offset_ft: (unit.interior_width || 20) / 2 - (unit.door_width || 40) / 2,
              width_ft: unit.door_width,
              height_ft: unit.door_height,
              type: (unit.door_type as DoorType) || 'bifold',
            }],
          }
        } else {
          doorConfig = {}
        }
      } else if (unitCount === 1 && Object.keys(buildingDoorConfig).length > 0) {
        // Single unit: use full building door config (all 4 walls)
        doorConfig = buildingDoorConfig
      } else if (unit.door_width && unit.door_height) {
        // Multi-unit non-double-row: use unit-level front door only
        doorConfig = {
          front: [{
            offset_ft: (unit.interior_width || 20) / 2 - (unit.door_width || 40) / 2,
            width_ft: unit.door_width,
            height_ft: unit.door_height,
            type: (unit.door_type as DoorType) || 'bifold',
          }],
        }
      } else {
        doorConfig = {}
      }

      // Use appropriate edge creation function based on template type
      let edges: UnitEdge[] | undefined
      let tHangarEdges: import('@/lib/templates/standard-t-hangar/edges').THangarUnitEdge[] | undefined

      if (templateType === 'double_row') {
        // For double_row, extract row ID and unit index from unit code
        // Unit codes are formatted as "A-01", "A-02", "B-01", "B-02" etc.
        const rowId = BuildingLoaderService.extractRowIdFromUnitCode(unit.unit_code)
        const unitIndexInRow = BuildingLoaderService.extractUnitIndexFromCode(unit.unit_code)
        const rowUnitCount = rowId === 'A' ? rowACounts : rowBCounts

        edges = createDoubleRowEdges(
          unitIndexInRow,
          rowUnitCount,
          rowId,
          buildingCode,
          doorConfig
        )
      } else if (templateType === 'standard_t_hangar') {
        // T-hangars use 8-edge system, not box edges
        // Reconstruct minimal THangarParams from typeConfig stored in DB
        const tParams: THangarParams = {
          name: '',
          code: buildingCode,
          latitude: 0,
          longitude: 0,
          bearing_deg: 0,
          floor_color: '',
          wall_color: '',
          unit_width_ft: typeConfig.unit_width_ft || unit.interior_width || 44.5,
          wing_depth_ft: typeConfig.wing_depth_ft || 21,
          tail_width_ft: typeConfig.tail_width_ft || 22.5,
          height_ft: unit.interior_height || 14,
          unit_count: unitCount,
          default_door_type: (unit.door_type as DoorType) || 'rolling',
          default_door_width_ft: unit.door_width || 40,
          default_door_height_ft: unit.door_height || 12,
          wall_thickness_ft: typeConfig.wall_thickness_ft || 0.5,
          unit_overrides: [],
        }

        // Build door spec from unit data
        const doorSpec: DoorSpec = unit.door_width && unit.door_height
          ? {
              offset_ft: (tParams.unit_width_ft - Math.min(unit.door_width, tParams.unit_width_ft - 4)) / 2,
              width_ft: Math.min(unit.door_width, tParams.unit_width_ft - 4),
              height_ft: unit.door_height,
              type: (unit.door_type as DoorType) || 'rolling',
            }
          : createDefaultTHangarDoorSpec(tParams)

        tHangarEdges = createTHangarEdges(index, unitCount, tParams, buildingCode, doorSpec)
        edges = [] // Box edges unused for T-hangars
      } else if (templateType === 'nested_t_hangar' && unit.unit_code.includes('-END-')) {
        // END units are data-only — orchestrator renders their walls
        edges = undefined
        tHangarEdges = undefined
      } else if (templateType === 'nested_t_hangar') {
        // Nested T-hangars use 8-edge system with internal back edges
        // Determine row from unit code (A or B)
        const rowId = BuildingLoaderService.extractRowIdFromUnitCode(unit.unit_code)
        const unitIndexInRow = BuildingLoaderService.extractUnitIndexFromCode(unit.unit_code)
        // Handle both new (unit_count) and old (row_a_unit_count) type_config shapes
        const rowACount = typeConfig.unit_count ?? typeConfig.row_a_unit_count ?? unitCount
        const rowBCount = rowACount - 1
        const rowUnitCount = rowId === 'A' ? rowACount : rowBCount

        const tParams: THangarParams = {
          name: '',
          code: `${buildingCode}-${rowId}`,
          latitude: 0,
          longitude: 0,
          bearing_deg: 0,
          floor_color: '',
          wall_color: '',
          unit_width_ft: typeConfig.unit_width_ft || unit.interior_width || 44.5,
          wing_depth_ft: typeConfig.wing_depth_ft || 21,
          tail_width_ft: typeConfig.tail_width_ft || 22.5,
          height_ft: unit.interior_height || 14,
          unit_count: rowUnitCount,
          default_door_type: (unit.door_type as DoorType) || 'rolling',
          default_door_width_ft: unit.door_width || 40,
          default_door_height_ft: unit.door_height || 12,
          wall_thickness_ft: typeConfig.wall_thickness_ft || 0.5,
          unit_overrides: [],
        }

        const doorSpec: DoorSpec = unit.door_width && unit.door_height
          ? {
              offset_ft: (tParams.unit_width_ft - Math.min(unit.door_width, tParams.unit_width_ft - 4)) / 2,
              width_ft: Math.min(unit.door_width, tParams.unit_width_ft - 4),
              height_ft: unit.door_height,
              type: (unit.door_type as DoorType) || 'rolling',
            }
          : createDefaultTHangarDoorSpec(tParams)

        tHangarEdges = createNestedTHangarEdges(unitIndexInRow, rowUnitCount, tParams, `${buildingCode}-${rowId}`, doorSpec)
        edges = [] // Box edges unused for T-hangars
      } else {
        // Default: use box unit edges (single_row, single_unit_community, etc.)
        edges = createBoxUnitEdges(index, unitCount, buildingCode, doorConfig)
      }

      return {
        id: unit.id,
        code: unit.unit_code,
        type: (unit.type as UnitType) || 'box_hangar',
        capacity_strategy: 'single' as const,
        interior_width: unit.interior_width || 20,
        interior_depth: unit.interior_depth || 40,
        interior_height: unit.interior_height || 20,
        door_width: unit.door_width || null,
        door_height: unit.door_height || null,
        door_type: (unit.door_type as DoorType) || undefined,
        offset_x: unit.offset_x || 0,
        offset_y: unit.offset_y || 0,
        unit_rotation: unit.unit_rotation || 0,
        edges,
        tHangarEdges,
      }
    })
  }

  /**
   * Extract row ID ('A' or 'B') from double-row unit code
   * Unit codes are formatted as "A-01", "B-03" etc.
   */
  private static extractRowIdFromUnitCode(unitCode: string): 'A' | 'B' {
    const firstChar = unitCode?.charAt(0)?.toUpperCase()
    return (firstChar === 'A' || firstChar === 'B') ? firstChar : 'A'
  }

  /**
   * Extract unit index (0-based) from double-row unit code
   * Unit codes are formatted as "A-01", "B-03" etc.
   */
  private static extractUnitIndexFromCode(unitCode: string): number {
    // Parse number after the dash (e.g., "A-01" -> 0, "B-03" -> 2)
    const match = unitCode?.match(/-(\d+)$/)
    if (match) {
      return parseInt(match[1], 10) - 1 // Convert to 0-based
    }
    return 0
  }
}
